import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  PROPERTY_CALC_FIELDS,
  baseDataStatusLabels,
  type BaseDataStatus,
  type CalculationAssumptions,
} from '@brimo/shared';
import { Banner, Button, Pill, Sheet, Textarea, type PillVariant } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { updateProperty, useOrganizationAssumptions } from '../api';
import { CardHead, FieldPair } from '../bits';
import type { PropertyRecord } from '../types';
import { CalcDefaultsFields } from './CalcDefaultsFields';
import {
  CALC_GROUPS,
  CARD_KEYS,
  calcDefaultsBody,
  calcDefaultsDraftOf,
  calcValueLabel,
  type CalcDefaultsDraft,
  type CalcDefaultsKey,
} from './calcDefaultsDraft';
import styles from './CalcDefaultsCard.module.css';
import dialogStyles from './dialogs.module.css';
import pageStyles from './PropertyRecordPage.module.css';

/**
 * Card „Objekt-Vorgaben" of the property record (Nico Kremer's feedback 01.11.2026, owner decision 02.11.2026,
 * D-361): the eight calculation defaults the units inherit plus „Kaufpreise ab" / „Rendite ab", the status pill and
 * the sheet „Objekt-Vorgaben" (`PATCH /properties/:id` `calcDefaults`). `?calc=1` opens the sheet directly — the
 * calculator's source links „aus Objekt-Vorgaben · ändern" lead here (D-360).
 */

/** The URL parameter that opens the sheet (`/properties/:id?calc=1`). */
export const CALC_SHEET_PARAM = 'calc';

const STATUS_VARIANTS: Record<BaseDataStatus, PillVariant> = {
  complete: 'success',
  partial: 'warning',
  empty: 'danger',
};

const ALL_KEYS: readonly CalcDefaultsKey[] = [...CALC_GROUPS.flatMap((group) => group.keys), 'storyline'];

export interface CalcDefaultsCardProps {
  record: PropertyRecord;
  onSaved: (record: PropertyRecord) => void;
}

export function CalcDefaultsCard({ record, onSaved }: CalcDefaultsCardProps) {
  const [params, setParams] = useSearchParams();
  const open = record.canEdit && params.get(CALC_SHEET_PARAM) === '1';
  const values = record.calcDefaults;

  function setOpen(next: boolean) {
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        if (next) copy.set(CALC_SHEET_PARAM, '1');
        else copy.delete(CALC_SHEET_PARAM);
        return copy;
      },
      { replace: true },
    );
  }

  return (
    <section className={pageStyles.card} aria-labelledby="property-calc-defaults">
      <CardHead
        eyebrow="Objekt-Vorgaben"
        id="property-calc-defaults"
        end={
          <Pill variant={STATUS_VARIANTS[values.status]} dot>
            {baseDataStatusLabels[values.status]}
          </Pill>
        }
      />
      <div className={styles.grid}>
        {CARD_KEYS.map((key) => (
          <FieldPair
            key={key}
            label={`${PROPERTY_CALC_FIELDS[key].label} (${PROPERTY_CALC_FIELDS[key].suffix})`}
            value={calcValueLabel(key, values[key])}
          />
        ))}
      </div>
      {record.canEdit ? (
        <div className={styles.actions}>
          <Button variant="secondary" size="small" onClick={() => setOpen(true)}>
            Objekt-Vorgaben bearbeiten
          </Button>
        </div>
      ) : null}
      <CalcDefaultsSheet
        open={open}
        record={record}
        onClose={() => setOpen(false)}
        onSaved={(saved) => {
          setOpen(false);
          onSaved(saved);
        }}
      />
    </section>
  );
}

export interface CalcDefaultsSheetProps {
  open: boolean;
  record: PropertyRecord;
  onClose: () => void;
  onSaved: (record: PropertyRecord) => void;
}

/** The organization's assumptions for the field hints; null without read access (`settings.organization`). */
export function useOrganizationHints(open: boolean): CalculationAssumptions | null {
  const mayRead = usePermissions().has('settings.organization');
  const assumptions = useOrganizationAssumptions(open && mayRead);
  return mayRead ? (assumptions.data?.current ?? null) : null;
}

/** Sheet „Objekt-Vorgaben" · {Objektname}: Kalkulation, Laufende Kosten je Einheit, Vertrieb, Storyline. */
export function CalcDefaultsSheet({ open, record, onClose, onSaved }: CalcDefaultsSheetProps) {
  const [draft, setDraft] = useState<CalcDefaultsDraft>(() => calcDefaultsDraftOf(record.calcDefaults));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const org = useOrganizationHints(open);

  useEffect(() => {
    if (open) {
      setDraft(calcDefaultsDraftOf(record.calcDefaults));
      setFields({});
      setError(null);
    }
  }, [open, record]);

  const change = (key: CalcDefaultsKey, value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));

  async function submit() {
    const { body, errors } = calcDefaultsBody(draft, ALL_KEYS);
    if (Object.keys(errors).length > 0) {
      setFields(errors);
      return;
    }
    setFields({});
    setError(null);
    setPending(true);
    try {
      onSaved(await updateProperty(record.id, { calcDefaults: body }));
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) setFields(err.fields);
      else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Objekt-Vorgaben"
      subtitle={record.name}
      height="full"
      className={styles.sheet}
      footer={
        <Button loading={pending} onClick={() => void submit()}>
          Speichern
        </Button>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className={dialogStyles.form}>
        {CALC_GROUPS.map((group) => (
          <CalcDefaultsFields
            key={group.title}
            title={group.title}
            keys={group.keys}
            draft={draft}
            fields={fields}
            org={org}
            onChange={change}
          />
        ))}
        <div className={dialogStyles.group}>
          <div className={dialogStyles.groupTitle}>Storyline</div>
          <Textarea
            label={PROPERTY_CALC_FIELDS.storyline.label}
            optional
            autoGrow
            rows={5}
            value={draft.storyline}
            error={fields['calcDefaults.storyline']}
            onChange={(event) => change('storyline', event.target.value)}
          />
        </div>
      </div>
    </Sheet>
  );
}
